/**
 * Disclosure labeling for exported studio images.
 *
 * MLS rules (and several state regulations) require virtually staged listing
 * photos to be labeled as such. The label is composited at download time only
 * — stored assets stay clean so they remain usable as inputs for further
 * edits/upscales.
 *
 * The pixels come from a fetched Blob (never a cross-origin <img>), so the
 * canvas is not tainted by the presigned S3 origin.
 */

async function decode(blob: Blob): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(blob);
    } catch {
      // fall through to the <img> path
    }
  }
  const url = URL.createObjectURL(blob);
  try {
    return await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error("Could not decode image"));
      img.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function withDisclosureLabel(blob: Blob, text: string): Promise<Blob> {
  const image = await decode(blob);
  const width = "naturalWidth" in image ? image.naturalWidth : image.width;
  const height = "naturalHeight" in image ? image.naturalHeight : image.height;

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return blob;
  ctx.drawImage(image, 0, 0);

  // Pill in the bottom-left corner, sized relative to the image.
  const fontSize = Math.max(13, Math.round(width * 0.022));
  ctx.font = `600 ${fontSize}px -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif`;
  const padX = Math.round(fontSize * 0.85);
  const padY = Math.round(fontSize * 0.5);
  const margin = Math.round(fontSize * 0.8);
  const textWidth = ctx.measureText(text).width;
  const w = Math.round(textWidth + padX * 2);
  const h = Math.round(fontSize + padY * 2);
  const x = margin;
  const y = height - h - margin;

  ctx.fillStyle = "rgba(15, 15, 15, 0.65)";
  ctx.beginPath();
  if (typeof ctx.roundRect === "function") {
    ctx.roundRect(x, y, w, h, h / 2);
  } else {
    ctx.rect(x, y, w, h);
  }
  ctx.fill();
  ctx.fillStyle = "#ffffff";
  ctx.textBaseline = "middle";
  ctx.fillText(text, x + padX, y + h / 2 + Math.round(fontSize * 0.05));

  // Keep the original format where the browser supports encoding it;
  // toBlob silently falls back to PNG otherwise, which is always safe.
  const type = ["image/jpeg", "image/webp"].includes(blob.type) ? blob.type : "image/png";
  return new Promise<Blob>((resolve) => {
    canvas.toBlob((out) => resolve(out || blob), type, 0.95);
  });
}

/** The label text appropriate for an asset, given the tool that produced it. */
export function disclosureTextFor(tool: string | null | undefined): string {
  return tool === "virtual-stage" ? "VIRTUALLY STAGED" : "AI-GENERATED";
}
